import React, { useState, useRef } from 'react';
import api, { getBackendUrl } from '../../config/api';
import toast from 'react-hot-toast';
import { 
  CloudArrowUpIcon, 
  // PhotoIcon, // Unused - removed
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

const ImageUpload = ({ 
  images = [], 
  onImagesChange, 
  primaryImageUrl, 
  onPrimaryImageChange,
  maxImages = 10,
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.post('/api/upload/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const result = response.data;
      // Convert existing images to objects if they're strings
      const existingImages = images.map(img => 
        typeof img === 'string' ? { imageUrl: img, filename: img.split('/').pop() } : img
      );
      const newImages = [...existingImages, ...result.images];
      
      onImagesChange(newImages);
      
      // Set first uploaded image as primary if no primary exists
      if (!primaryImageUrl && result.images.length > 0) {
        onPrimaryImageChange(result.images[0].imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const removeImage = async (imageUrl) => {
    try {
      const filename = imageUrl.split('/').pop();
      await api.delete(`/api/upload/${filename}`);

      const updatedImages = images.filter(img => {
        const imgUrl = typeof img === 'string' ? img : img.imageUrl;
        return imgUrl !== imageUrl;
      });
      onImagesChange(updatedImages);
      
      // If removing primary image, set new primary
      if (primaryImageUrl === imageUrl) {
        const newPrimary = updatedImages.length > 0 ? (typeof updatedImages[0] === 'string' ? updatedImages[0] : updatedImages[0].imageUrl) : '';
        onPrimaryImageChange(newPrimary);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete image. Please try again.');
    }
  };

  const setAsPrimary = (imageUrl) => {
    onPrimaryImageChange(imageUrl);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver 
            ? 'border-marine-400 bg-marine-50' 
            : 'border-gray-300 hover:border-marine-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        style={{
          borderColor: dragOver ? '#3b82f6' : '#475569',
          backgroundColor: dragOver ? '#1e40af' : 'transparent',
          cursor: 'pointer'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={uploading || images.length >= maxImages}
        />
        
        {uploading ? (
          <div className="space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-marine-600 mx-auto"></div>
            <p className="text-sm" style={{color: '#cbd5e1'}}>Uploading images...</p>
          </div>
        ) : (
          <div className="space-y-2">
            <CloudArrowUpIcon className="h-8 w-8 mx-auto" style={{color: '#64748b'}} />
            <div>
              <p className="text-sm font-medium" style={{color: '#f8fafc'}}>
                {images.length >= maxImages 
                  ? `Maximum ${maxImages} images reached`
                  : 'Click to upload or drag and drop'
                }
              </p>
              <p className="text-xs" style={{color: '#94a3b8'}}>
                PNG, JPG, GIF up to 10MB each
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Image Grid */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((image, index) => {
            const imageUrl = typeof image === 'string' ? image : image.imageUrl;
            // const imageData = typeof image === 'string' ? { imageUrl, filename: image.split('/').pop() } : image; // Unused - removed
            return (
            <div key={index} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden border-2" style={{
                borderColor: primaryImageUrl === imageUrl ? '#3b82f6' : '#475569'
              }}>
                <img
                  src={imageUrl.startsWith('http') ? imageUrl : `${getBackendUrl()}${imageUrl}`}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Primary Badge */}
              {primaryImageUrl === imageUrl && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-marine-600 text-white">
                    <CheckIcon className="h-3 w-3 mr-1" />
                    Primary
                  </span>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                  {primaryImageUrl !== imageUrl && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAsPrimary(imageUrl);
                      }}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                      title="Set as primary"
                    >
                      <CheckIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeImage(imageUrl);
                    }}
                    className="p-2 bg-red-500 rounded-full shadow-lg hover:bg-red-600"
                    title="Delete image"
                  >
                    <XMarkIcon className="h-4 w-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
